import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import FormTextArea from "../../components/formFields/FormTextArea";
import {
  createUsefulLinks,
  getUsefulLinksByID,
  updateUsefulLinks,
} from "./services/usefullinks.services";
import { clearUsefulLinksDetails } from "./usefullinksSlice";
import FormCheckbox from "../../components/formFields/FormCheckbox";

const CreateUsefulLinks: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const UsefulLinksID = location.state?.UsefulLinksID;
  const UsefulLinksDetail = useAppSelector(
    (state) => state.usefullinks.UsefulLinksDetail,
  );
  const loading = useAppSelector((state) => state.usefullinks.loading);

  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    if (UsefulLinksID) {
      dispatch(getUsefulLinksByID(UsefulLinksID));
    }
    return () => {
      dispatch(clearUsefulLinksDetails());
    };
  }, [UsefulLinksID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [UsefulLinksDetail]);

  const handleSubmit = async (values: any) => {
    try {
      if (UsefulLinksID) {
        const editPayload = {
          ID: UsefulLinksID,
          Title: values.Title ? values.Title : "",
          Description: values.Description ? values.Description : "",
          WebsiteTitle: values.WebsiteTitle ? values.WebsiteTitle : "",
          WebsiteLink: values.WebsiteLink ? values.WebsiteLink : "",
          IsDisplayMain: values.IsDisplayMain,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateUsefulLinks(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate(-1);
        }
      } else {
        const insertPayload = {
          Title: values.Title ? values.Title : "",
          Description: values.Description ? values.Description : "",
          WebsiteTitle: values.WebsiteTitle ? values.WebsiteTitle : "",
          WebsiteLink: values.WebsiteLink ? values.WebsiteLink : "",
          IsDisplayMain: values.IsDisplayMain,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createUsefulLinks(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate("/usefullinks");
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={UsefulLinksDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 20 }}
                gap={{ rows: 5, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
              >
                <GridLayoutItem colSpan={3}>
                  <Typography.h4>
                    {UsefulLinksID ? "Update Useful Links" : "Add Useful Links"}
                  </Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="Title"
                    label="Title"
                    placeholder="Title"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="WebsiteTitle"
                    label="Website Title "
                    placeholder="Website Title"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="WebsiteLink"
                    label="Website Link"
                    placeholder="Website Link"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="Description"
                    label="Description"
                    placeholder="Description"
                    component={FormTextArea}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  style={{
                    marginTop: "36px",
                    background: "#edeaea",
                    borderRadius: 10,
                    padding: "0 10px 15px 10px",
                    fontWeight: 500,
                    color: "black",
                    height: "50px",
                  }}
                >
                  <Field
                    labelClassName="ms-2"
                    name="IsDisplayMain"
                    maxLength="300"
                    label="Is Display Main"
                    component={FormCheckbox}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  style={{
                    paddingTop: "8px",
                  }}
                >
                  <Field
                    name={`IsActive`}
                    label="Status"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status?.value,
                        label: status?.label,
                      };
                    })}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  colSpan={3}
                  style={{
                    marginTop: "20px",
                    textAlign: "end",
                  }}
                >
                  <div>
                    <ButtonWithLoading
                      label={UsefulLinksID ? "Update" : "Save"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />

                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 10 }}
                      onClick={() => navigate("/usefullinks")}
                    >
                      Cancel
                    </Button>
                  </div>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateUsefulLinks;
